import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hook';
import { selectLmsLists } from '../../store/features/lmsSlice';
import {
  changeTimestamp,
  handleCopyClipBoard,
} from '../../api/utils/util-func';
import { courseIdDetermination } from '../../api/utils/lms/ExtendSetBtnLink';
import { LoadingSpinner } from '../Loading/LoadingSpinner';
import { Menu, Tooltip } from 'antd';
import { SidebarItems } from './sidebarItem';
import {
  DocumentDuplicateIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline';
import ModalAlert from '../modal/ModalAlert';
import { openAlert } from '../../store/features/modalSlice';

const Sidebar = ({ classId }: { classId: string }) => {
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const lmsLists = useAppSelector(selectLmsLists);
  const [isLoading, setIsloading] = useState(false);
  const [menuExposure, setMenuExposure] = useState<'home' | 'church' | ''>('');

  /**
   * 현재 코스의 분류 결정
   * @description setMenuExposure 함수의 인자
   * - "home" : 젭스영어교실 베이직
   * - "church" : 교회학교 HFHJ, WMW
   * - "" : 그 외
   */
  useEffect(() => {
    setIsloading(true);
    if (lmsLists) {
      setMenuExposure(courseIdDetermination(classId, lmsLists));
      setIsloading(false);
    }
  }, [lmsLists]);

  /**
   * 사이드 메뉴의 하이라이트 표시
   * @returns 현재 메뉴
   */
  const nowMenu = useMemo(() => {
    const urlArr = pathname.split('/');

    if (urlArr.length < 3 || urlArr[1] !== 'lms') {
      console.error('잘못된 접속입니다.', pathname);
      return;
    }

    const base = urlArr[3];
    switch (base) {
      case 'dashboard':
      case 'course-management':
      case 'take-class':
      case 'reference-room':
      case 'signup-management':
      case 'event':
        return base;
      case 'todo-management':
        const subType = urlArr[4];
        return [
          'recitation',
          'jebson',
          'jebspl',
          'jebs_ai_recitation',
        ].includes(subType)
          ? subType
          : undefined;
      case 'recitation':
        const menu = urlArr[4];
        switch (menu) {
          case 'dashboard':
            return 'recitation-dashboard';
          case 'list':
            return 'recitation-list';
          default:
            return;
        }
      case 'migration':
        return 'signup-management';
      default:
        return;
    }
  }, [pathname]);

  const handleCopyClipboard = (text: string) => {
    handleCopyClipBoard(text).then((result) => {
      if (result) {
        dispatch(openAlert({ message: '클래스 코드가 복사되었습니다.' }));
      } else {
        dispatch(openAlert({ message: '클래스 코드 복사에 실패했습니다.' }));
      }
    });
  };
  return isLoading ? (
    <LoadingSpinner message="데이터 조회중" />
  ) : (
    <div>
      {lmsLists && (
        <div className="border-b bg-gray0 px-5 py-3 xl:bg-white xl:px-7 xl:py-7">
          <Link to={`./dashboard`}>
            <div className="text-lg font-semibold">{lmsLists.course?.name}</div>
          </Link>
          <div className="mt-2 flex items-center justify-start gap-1">
            <div>클래스 코드</div>
            <Tooltip
              title="‘클래스 코드’는 선생님이 개설한 클래스의 고유 번호로, 학생 계정에 입력해야 소속이 확인됩니다. 클래스가 변경되면 담당 선생님께 코드를 문의해 수정해 주세요."
              placement="rightTop"
            >
              <QuestionMarkCircleIcon className="h-5 w-5" />
            </Tooltip>
          </div>
          <div
            className="flex cursor-pointer items-center gap-1 text-sm hover:text-skyblue"
            onClick={() => handleCopyClipboard(classId)}
          >
            {classId}
            <DocumentDuplicateIcon className="h-5 w-5" />
          </div>
          <div className="mt-2 text-sm">
            {changeTimestamp(lmsLists?.created_at)} 개설
          </div>
        </div>
      )}
      <Menu
        mode="inline"
        defaultOpenKeys={[
          'management',
          'todo',
          'student',
          'recitation-history',
        ]}
        selectedKeys={[nowMenu]}
        items={SidebarItems(menuExposure)}
      />
    </div>
  );
};

export default Sidebar;
